// Licensed to the .NET Foundation under one or more agreements.
// The .NET Foundation licenses this file to you under the MIT license.

import { loc, meta, options } from './helper'
import { html, render, TemplateResult } from 'lit-html'

type SearchHit = {
  href: string
  title: string
  summary: string
  keywords: string
  tags: string[]
  tagslugged: string[]
  img: string
}

let query

/**
 * Support full-text-search
 */
export async function enableSearch() {
  const searchQuery = getSearchQueryBox()
  const relHref = meta('docfx:rel') || ''
  const worker = new Worker(relHref + 'content/javascripts/search-worker.min.js', { type: 'module' })

  worker.onerror = event => {
    console.error('Error occurred at search-worker. message: ' + event.message)
  }

  worker.onmessage = function(oEvent) {
    switch (oEvent.data.e) {
      case 'index-ready':
        searchQuery.disabled = false
        searchQuery.addEventListener('input', onSearchQueryInput)
        searchQuery.addEventListener('keypress', function(e) {
          if (e.key === 'Enter') {
            event.preventDefault()
          }
        })
        window.docfx.searchReady = true
        break
      case 'query-ready':
        const element = document.getElementById("main-div")
        if (element) {
          element.classList.add("hidden")
        }
        const searchResults = document.getElementById("search-results")
        if (searchResults) {
          searchResults.classList.remove("hidden")
        }
        renderSearchResults(oEvent.data.d)
        break
    }
  }

  const { lunrLanguages } = await options()
  worker.postMessage({ init: { lunrLanguages } })

  function onSearchQueryInput() {
    query = searchQuery.value

    if (query === '') {
      const element = document.getElementById("main-div")
      if (element) {
        element.classList.remove("hidden")
      }
      const searchResults = document.getElementById("search-results")
      if (searchResults) {
        searchResults.classList.add("hidden")
      }
    } else {
      const additiveQuery = query.replace(/\s+/g, ' ').split(' ').map(w => '+' + w).join(' ')
      worker.postMessage({ q: additiveQuery })
    }
  }

  function extractContentBrief(content) {
    const briefOffset = 300
    const words = query.split(/\s+/g)
    const queryIndex = content.toLowerCase().indexOf(words[0].toLowerCase())
    if (queryIndex > briefOffset) {
      return '...' + content.slice(queryIndex - briefOffset, queryIndex + briefOffset) + '...'
    } else if (queryIndex <= briefOffset) {
      return content.slice(0, queryIndex + briefOffset) + '...'
    }
  }

  function renderSearchResults(hits: SearchHit[]) {
    render(
      renderPage(),
      document.getElementById('search-results'))

    function renderPage(): TemplateResult {
      if (hits.length === 0) {
        return html`<div class="text-gray-400 py-2">${loc('searchNoResults', { query })}</div>`
      }

      const items = html`
        <div class="text-gray-400 py-2">${loc('searchResultsCount', { count: hits.length.toString(), query })}</div>
        <div class="sr-items">${hits.map(hit => {
          const itemHref = relHref + hit.href
          const itemBrief = hit.summary ? extractContentBrief(hit.summary) : ''
          if (hit.img === "") {
            return html`
            <div class="sr-item mb-3">
              <div class="pb-1">
                <a class="text-myblue text-xl border-b-[1px] border-b-myblue font-semibold hover:border-b-sky-600 hover:text-sky-600" href="${itemHref}">${mark(hit.title, query)}</a>
              </div>
              ${markTags(query, hit.tags, hit.tagslugged)}
              <div class="text-gray-200 text-sm">${mark(itemBrief, query)}</div>
            </div>`
          } else {
            return html`
            <div class="sr-item mb-3">
              <div class="w-[75px] aspect-square overflow-hidden relative float-left mr-4">
                <a href="${itemHref}">
                    <img src="${hit.img}" class="w-full h-full object-cover object-center rounded-lg">
                </a>
              </div>
              <div class="pb-1">
                <a class="text-myblue text-xl border-b-[1px] border-b-myblue font-semibold hover:border-b-sky-600 hover:text-sky-600" href="${itemHref}">${mark(hit.title, query)}</a>
              </div>
              ${markTags(query, hit.tags, hit.tagslugged)}
              <div class="text-gray-200 text-sm">${mark(itemBrief, query)}</div>
            </div>`
          }
          })}
        </div>`

      return html`${items}`
    }
  }
}

function makeTagLinks(query: string, tags: string[], tagslugged: string[]): TemplateResult {
  const result = []
  for (let i = 0; i < tags.length; i++) {
    const tag = tags[i]
    const tagURL = tagslugged[i]
    result.push(html`<a class="bg-gray-700 rounded-md px-[5px] py-[2px] text-myblue text-sm hover:text-sky-600 mr-1 mt-1" href="/tags/${tagURL}">#${mark(tag, query)}</a>`)
  }
  return html`${result}`
}

function markTags(query: string, tags: string[], tagslugged: string[]): TemplateResult {
  if (tags == null)
    return html``

  const tagHTML = makeTagLinks(query, tags, tagslugged)

  const result = []
  result.push(html`<div class="flex flex-wrap items-center gap-1 py-1">${tagHTML}</div>`)
  return html`${result}`
}

function mark(text: string, query: string): TemplateResult {
  const words = query.split(/\s+/g)
  const wordsLower = words.map(w => w.toLowerCase())
  const textLower = text.toLowerCase()
  const result = []
  let lastEnd = 0
  for (let i = 0; i < wordsLower.length; i++) {
    const word = wordsLower[i]
    const index = textLower.indexOf(word, lastEnd)
    if (index >= 0) {
      result.push(html`${text.slice(lastEnd, index)}`)
      result.push(html`<b class="font-extrabold">${text.slice(index, index + word.length)}</b>`)
      lastEnd = index + word.length
    }
  }
  result.push(html`${text.slice(lastEnd)}`)
  return html`${result}`
}

function getSearchQueryBox(): HTMLInputElement {
  let searchQuery = document.getElementById('search-query') as HTMLInputElement
  if (!searchQuery || !window.Worker) {
    return
  }

  const childElement = document.getElementById('search-query');
  if (childElement) {
    const parentIsVisible = isParentVisible(childElement)
    // We're on mobile, so get the 2nd search-query
    if (!parentIsVisible) {
      searchQuery = document.getElementById('search-query-mobile') as HTMLInputElement
    }
  }
  return searchQuery
}

function isParentVisible(element: HTMLElement | null): boolean {
  if (!element) {
    return false; // No element provided
  }

  let currentElement: HTMLElement | null = element.parentElement

  while (currentElement) {
    const computedStyle = window.getComputedStyle(currentElement)

    // Check for display: none
    if (computedStyle.display === 'none') {
      return false
    }

    // Check for visibility: hidden
    if (computedStyle.visibility === 'hidden') {
      return false;
    }

    // Check for opacity: 0
    if (parseFloat(computedStyle.opacity) === 0) {
      return false
    }

    // Check if the element takes up space (e.g., if it's rendered)
    // This helps catch cases where an element might be technically visible but has no dimensions
    if (currentElement.offsetWidth === 0 && currentElement.offsetHeight === 0) {
      return false
    }

    currentElement = currentElement.parentElement // Move up to the next parent
  }

  // If we reached the document body or top-level, and no hidden properties were found, it's visible.
  return true
}
